import { useTranslation } from 'react-i18next';

export default function GuidePage() {
  const { t } = useTranslation('guide');

  const quickInfo = [
    { labelKey: 'quickInfo.flight.label', valueKey: 'quickInfo.flight.value' },
    { labelKey: 'quickInfo.timezone.label', valueKey: 'quickInfo.timezone.value' },
    { labelKey: 'quickInfo.visa.label', valueKey: 'quickInfo.visa.value' },
    { labelKey: 'quickInfo.currency.label', valueKey: 'quickInfo.currency.value' },
  ];

  const peakSeasonItems = [
    'season.peak.item1',
    'season.peak.item2',
    'season.peak.item3',
    'season.peak.item4',
  ];

  const lowSeasonItems = [
    'season.low.item1',
    'season.low.item2',
    'season.low.item3',
    'season.low.item4',
  ];

  const budgetRows = [
    { itemKey: 'budget.flight', economyKey: 'budget.flightEconomy', luxuryKey: 'budget.flightLuxury' },
    { itemKey: 'budget.accommodation', economyKey: 'budget.accommodationEconomy', luxuryKey: 'budget.accommodationLuxury' },
    { itemKey: 'budget.transfer', economyKey: 'budget.transferEconomy', luxuryKey: 'budget.transferLuxury' },
    { itemKey: 'budget.food', economyKey: 'budget.foodEconomy', luxuryKey: 'budget.foodLuxury' },
    { itemKey: 'budget.activities', economyKey: 'budget.activitiesEconomy', luxuryKey: 'budget.activitiesLuxury' },
  ];

  const checklistGroups = [
    { catKey: 'checklist.documents.title', itemKeys: ['checklist.documents.item1', 'checklist.documents.item2', 'checklist.documents.item3', 'checklist.documents.item4'] },
    { catKey: 'checklist.clothing.title', itemKeys: ['checklist.clothing.item1', 'checklist.clothing.item2', 'checklist.clothing.item3', 'checklist.clothing.item4'] },
    { catKey: 'checklist.gear.title', itemKeys: ['checklist.gear.item1', 'checklist.gear.item2', 'checklist.gear.item3', 'checklist.gear.item4'] },
    { catKey: 'checklist.others.title', itemKeys: ['checklist.others.item1', 'checklist.others.item2', 'checklist.others.item3', 'checklist.others.item4'] },
  ];

  const notes = [
    'notes.item1',
    'notes.item2',
    'notes.item3',
    'notes.item4',
    'notes.item5',
  ];

  return (
    <div className="pt-16">
      {/* Hero */}
      <div className="relative h-64 flex items-center justify-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1920&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a4c6b]/70" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">{t('hero.title')}</h1>
          <p className="text-white/80">{t('hero.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {quickInfo.map((info, i) => (
            <div key={i} className="bg-[#f0f9f7] rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500">{t(info.labelKey)}</p>
              <p className="font-semibold text-[#0a4c6b] mt-1">{t(info.valueKey)}</p>
            </div>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-12">
          <section>
            <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4">✈️ {t('sections.gettingThere.title')}</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed">
                {t('sections.gettingThere.description')}
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
                <p className="text-sm text-yellow-800 font-medium">{t('sections.gettingThere.tip.title')}</p>
                <p className="text-sm text-yellow-700 mt-1">{t('sections.gettingThere.tip.description')}</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4">🏝️ {t('sections.season.title')}</h2>
            <p className="text-gray-600 leading-relaxed">
              {t('sections.season.description')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="border rounded-xl p-4">
                <h4 className="font-semibold text-[#0a4c6b] mb-2">{t('sections.season.peakTitle')}</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {peakSeasonItems.map((key, idx) => <li key={idx}>• {t(key)}</li>)}
                </ul>
              </div>
              <div className="border rounded-xl p-4">
                <h4 className="font-semibold text-[#2ec4b6] mb-2">{t('sections.season.lowTitle')}</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {lowSeasonItems.map((key, idx) => <li key={idx}>• {t(key)}</li>)}
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4">💰 {t('sections.budget.title')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-xl overflow-hidden">
                <thead className="bg-[#f0f9f7]">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">{t('sections.budget.headerItem')}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">{t('sections.budget.headerEconomy')}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">{t('sections.budget.headerLuxury')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {budgetRows.map((row, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3">{t(row.itemKey)}</td>
                      <td className="px-4 py-3">{t(row.economyKey)}</td>
                      <td className="px-4 py-3">{t(row.luxuryKey)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4">📋 {t('sections.checklist.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {checklistGroups.map((group, i) => (
                <div key={i} className="border rounded-xl p-4">
                  <h4 className="font-semibold text-[#0a4c6b] mb-2">{t(group.catKey)}</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {group.itemKeys.map((key, j) => <li key={j}>☐ {t(key)}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0d1b2a] mb-4">⚠️ {t('sections.notes.title')}</h2>
            <div className="space-y-3">
              {notes.map((key, i) => (
                <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-orange-500 text-lg">⚠️</span>
                  <p className="text-sm text-gray-700">{t(key)}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* 指南頁底部 CTA 模組 */}
      <section className="bg-[#0d1b2a] text-white py-16 px-6 text-center">
        <p className="text-[#B8902F] text-sm font-medium tracking-widest uppercase mb-3">
          {t('cta.eyebrow')}
        </p>
        <h2 className="text-3xl font-bold mb-4 font-['Playfair_Display']">
          {t('cta.title')}
        </h2>
        <p className="text-gray-300 mb-8 max-w-md mx-auto text-base">
          {t('cta.description')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.location.href = '/plan'}
            className="px-8 py-4 bg-[#B8902F] text-white font-medium rounded-lg hover:bg-[#96771f] transition-colors"
          >
            {t('cta.contactButler')} →
          </button>
          <button
            onClick={() => window.location.href = '/properties'}
            className="px-8 py-4 border border-white text-white font-medium rounded-lg hover:bg-white hover:text-[#0d1b2a] transition-colors"
          >
            {t('cta.browseProperties')}
          </button>
        </div>
        <p className="mt-6 text-xs text-gray-500">{t('cta.footer')}</p>
      </section>
    </div>
  );
}
